"use client";

import React, { useEffect, useState, useRef } from "react";
import Link from "next/link";
import LeadCapturePopup from "@/components/forms/LeadCapturePopup";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
} from "framer-motion";

import {
  ArrowRight,
  Download,
  LayoutDashboard,
  FlaskConical,
  Package,
  CheckSquare,
  FileText,
  TrendingUp,
  AlertTriangle,
  ShieldCheck,
  Utensils,
  Search,
  Bell,
  Menu,
  BarChart3,
  LineChart,
  Zap,
  Wrench,
  Building2,
  Armchair,
  Rocket,
  Bed,
  FlaskConical as Beaker,
  Settings,
  Activity,
  Smile,
  Lock,
  Fingerprint,
  Sliders,
  Map,
  Scissors,
  Target,
  Cpu,
  Box,
  Layout,
  Coins,
  ClipboardList,
  Database,
  Laptop,
  History,
  QrCode,
  MessageSquare,
  List,
  Sliders as SlidersIcon,
  Drum,
  Thermometer,
  Layers,
  Clock,
  Calculator,
  ShieldAlert,
  Book,
  RefreshCw,
  Eye,
} from "lucide-react";

import { Industry } from "@/types";

// ─────────────────────────────────────────────────────────────
// Icon Renderer
// ─────────────────────────────────────────────────────────────

const IconRenderer = ({
  name,
  size = 20,
  className = "",
}: {
  name: string;
  size?: number;
  className?: string;
}) => {
  const icons: Record<string, any> = {
    LayoutDashboard, FlaskConical, Package, CheckSquare, FileText,
    TrendingUp, AlertTriangle, ShieldCheck, Utensils, Zap, Wrench,
    Building2, Armchair, Rocket, Bed, Beaker, Settings, Activity,
    Smile, Lock, Fingerprint, Sliders, Map, Scissors, Target, Cpu,
    Box, Layout, Coins, ClipboardList, Database, Laptop, History,
    QrCode, MessageSquare, List, SlidersIcon, Drum, Thermometer,
    Layers, Clock, Calculator, ShieldAlert, Book, RefreshCw, Eye,
  };

  const Icon = icons[name] || LayoutDashboard;
  return <Icon size={size} className={className} />;
};

interface IndustryDetailHeroProps {
  industry: Industry;
  onTalkToExpert?: () => void;
}

export function IndustryDetailHero({ industry, onTalkToExpert }: IndustryDetailHeroProps) {
  const [mounted, setMounted] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setMounted(true); }, []);

  // ─── Scroll Animations ────────────────────────────────────
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  const springConfig = { stiffness: 85, damping: 25, restDelta: 0.001 };

  const y = useSpring(useTransform(scrollYProgress, [0, 0.4], [0, -15]), springConfig);
  const scale = useSpring(useTransform(scrollYProgress, [0, 0.4], [1, 0.985]), springConfig);

  // ─── Dashboard Fallback ───────────────────────────────────
  const dashboard = industry.dashboard || {
    title: "Operations Overview",
    subtitle: "Real-time industry monitoring",
    kpis: [
      { label: "REVENUE", value: "₹45.2L", trend: "↗ +12.5%", icon: "TrendingUp", color: "#10b981" },
      { label: "ACTIVE", value: "12,450", trend: "↗ +4.1%", icon: "Activity", color: "#3b82f6" },
      { label: "PENDING", value: "142", trend: "↘ -2.5%", icon: "Package", color: "#f59e0b" },
      { label: "ALERTS", value: "28", trend: "Requires Action", icon: "AlertTriangle", color: "#ef4444" },
    ],
    chartTitle: "PERFORMANCE GROWTH",
    sidebarItems: [
      { label: "Dashboard", icon: "LayoutDashboard" },
      { label: "Inventory", icon: "Package" },
      { label: "Analytics", icon: "BarChart3" },
    ],
    linePath: "M0,25 C15,25 25,18 40,18 C50,18 55,14 65,11 C75,7 85,3 100,5",
    areaPath: "M0,25 C15,25 25,18 40,18 C50,18 55,14 65,11 C75,7 85,3 100,5 L100,30 L0,30 Z",
    lineColor: "#3b82f6",
    barHeights: [55, 40, 30, 45, 68, 35, 60],
    barTitle: "MACHINE LOAD",
    barColor: "#3b82f6",
  };

  return (
    <div ref={sectionRef} className="relative w-full bg-[#fafcff]">

      {/* ─── HERO SECTION ──────────────────────────────────── */}
      <section
        className="
          relative w-full
          /* FIX 1: no forced full-vh on mobile; desktop keeps the tall layout */
          md:min-h-[calc(100vh-80px)]
          flex flex-col justify-between items-center
          overflow-hidden
          /* FIX 2: generous vertical padding for better visibility */
          pt-10 md:pt-16
          pb-6 md:pb-10
          z-10
          px-4 md:px-8
        "
      >
        {/* Glow BG */}
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#EC4899]/5 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-[20%] right-[-10%] w-[35%] h-[35%] bg-[#7F77DD]/8 rounded-full blur-[100px] pointer-events-none" />

        <div className="w-full max-w-[1280px] mx-auto flex flex-col items-center flex-1 justify-center">

          {/* ─── HEADING ───────────────────────────────────── */}
          <div className="flex flex-col items-center text-center w-full mb-8 md:mb-10 shrink-0">
            {/* Badge */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.05, duration: 0.4 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4 border bg-blue-50/50 border-blue-100/80"
            >
              <div className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2C78B9] opacity-40" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#2C78B9]" />
              </div>
              <span className="text-[9px] md:text-[10px] font-bold text-[#2C78B9] tracking-[0.1em] uppercase">
                {industry.category} · INDUSTRY SOLUTION
              </span>
            </motion.div>

            {/* Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="
                text-[24px] sm:text-[34px] md:text-[40px] lg:text-[44px]
                font-black tracking-[-0.03em] text-[#0f172a] leading-[1.05]
                flex flex-wrap items-center justify-center
                gap-x-2 md:gap-x-3 max-w-[900px]
              "
            >
              <span>{industry.title}</span>
              {/* <span className="text-[#2C78B9]">ERP Software</span> */}
            </motion.h1>
          </div>

          {/* ─── DASHBOARD ─────────────────────────────────── */}
          <motion.div
            style={{ y, scale }}
            className="
              w-full max-w-[1150px]
              relative z-20
              flex items-center justify-center
              /* FIX 3: increased height on mobile to prevent clipping */
              max-h-[600px] sm:max-h-[700px] md:max-h-none
              /* FIX 4: ensure top border/shadow is not clipped */
              overflow-visible
              pt-4 md:pt-0
            "
          >
            {/* Outer Frame */}
            <div
              className="
                rounded-t-[22px] md:rounded-t-[28px]
                rounded-b-none
                p-[6px] md:p-[15px] pb-0
                bg-[#090d16]
                shadow-[0_30px_70px_-15px_rgba(0,0,0,0.15)]
                w-full border-b-0
              "
            >
              {/* Inner white card */}
              <div
                className="
                  bg-white
                  rounded-t-[16px] md:rounded-t-[20px]
                  rounded-b-none
                  overflow-hidden
                  flex flex-col md:flex-row
                  w-full relative
                "
              >
                {/* ─── SIDEBAR ───────────────────────────── */}
                <div
                  className="
                    hidden lg:flex w-[210px]
                    bg-white border-r border-[#f1f5f9]
                    flex-col py-5 px-3 shrink-0 z-10
                  "
                >
                  <div className="flex items-center gap-2 text-[#2C78B9] font-black text-[18px] px-2 mb-6">
                    <IconRenderer name={industry.icon} size={20} className="text-[#2C78B9]" />
                    Mx-ERP
                  </div>

                  <div className="text-[9px] text-[#94a3b8] font-bold tracking-[0.1em] mb-3 px-2">MAIN MENU</div>

                  <div className="flex flex-col gap-0.5 flex-1">
                    <div className="flex items-center gap-2.5 text-[12px] font-bold p-2.5 bg-[#f8fafc] text-[#0f172a] rounded-lg mb-1.5">
                      <div className="p-1 bg-white rounded shadow-sm">
                        <LayoutDashboard size={14} className="text-[#2C78B9]" />
                      </div>
                      Dashboard
                    </div>
                    {dashboard.sidebarItems.map((item, i) => (
                      <div key={i} className="flex items-center gap-2.5 text-[12px] font-semibold p-2.5 text-[#64748b] hover:bg-[#f8fafc] rounded-lg transition-colors cursor-pointer">
                        <div className="p-1">
                          <IconRenderer name={item.icon} size={14} className="text-[#94a3b8]" />
                        </div>
                        {item.label}
                      </div>
                    ))}
                  </div>

                  <div className="mt-auto bg-[#f8fafc] p-2.5 rounded-lg flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-[#2C78B9] text-white flex items-center justify-center font-bold text-[12px]">PM</div>
                    <div>
                      <div className="text-[11px] font-bold text-[#0f172a]">Manager</div>
                      <div className="text-[9px] text-[#64748b] font-medium mt-0.5">USAGE: 75% OF PLAN</div>
                    </div>
                  </div>
                </div>

                {/* ─── MAIN CONTENT ──────────────────────── */}
                <div className="flex-1 bg-[#fcfdff] flex flex-col overflow-hidden relative">
                  {/* Topbar */}
                  <div className="h-[48px] md:h-[56px] border-b border-[#f1f5f9] flex items-center justify-between px-3 md:px-5 bg-white shrink-0">
                    <div className="lg:hidden flex items-center gap-1.5 text-[#2C78B9] font-black text-[14px] md:text-[15px]">
                      <IconRenderer name={industry.icon} size={16} />
                      Mx-ERP
                    </div>
                    <div className="hidden lg:flex flex-1 max-w-[320px] relative">
                      <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
                      <input
                        type="text"
                        placeholder="Search reports, data..."
                        className="w-full bg-[#f8fafc] border-none outline-none text-[11px] py-2 pl-9 pr-4 rounded-full text-[#0f172a] font-medium"
                        readOnly
                      />
                    </div>
                    <div className="flex items-center gap-3 text-[#64748b]">
                      <LineChart size={16} className="hidden lg:block" />
                      <Bell size={16} className="hidden lg:block" />
                      <Menu size={18} className="lg:hidden" />
                    </div>
                  </div>

                  {/* Dashboard Body */}
                  {/*
                    FIX 4: Mobile — scale the entire dashboard body down so it
                    fits inside the capped max-h container, identical strategy
                    to DashboardShowcase's mobile handling.
                  */}
                  <div className="flex-1 overflow-hidden relative">
                    {/* MOBILE: scale-down wrapper */}
                    <div
                      className="
                        block md:hidden
                        origin-top-left
                        scale-[0.85] sm:scale-[0.95]
                      "
                      style={{ width: "118%", marginBottom: "-120px" }}
                    >
                      <DashboardBody dashboard={dashboard} />
                    </div>

                    {/* DESKTOP: natural size */}
                    <div className="hidden md:block h-full">
                      <DashboardBody dashboard={dashboard} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Fade */}
        <div className="absolute bottom-0 left-0 right-0 h-[60px] md:h-[130px] bg-gradient-to-t from-[#fafcff] via-[#fafcff]/90 to-transparent pointer-events-none z-30" />
      </section>

      {/* ─── SECOND SECTION ────────────────────────────────── */}
      <section className="relative w-full bg-white py-12 md:py-16 border-t border-[#f1f5f9] z-10 px-4 md:px-8">
        <div className="w-full max-w-[720px] mx-auto flex flex-col items-center text-center">
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="text-[15px] md:text-[18px] text-[#475569] leading-[1.8] mb-8 font-medium"
          >
            {industry.longDescription}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full"
          >
            <button
              onClick={() => { setDemoOpen(true); }}
              className="w-full sm:w-auto bg-[#2C78B9] text-white rounded-full px-8 py-4 font-bold text-[15px] shadow-[0_8px_24px_rgba(44,120,185,0.25)] hover:scale-105 transition-transform duration-300 flex items-center justify-center gap-2"
            >
              Request Free Demo
              <ArrowRight size={18} />
            </button>
            <button
              onClick={() => { onTalkToExpert?.();}}
              className="w-full sm:w-auto bg-white border-[2px] border-[#e2e8f0] text-[#0f172a] rounded-full px-8 py-4 font-bold text-[15px] hover:border-[#2C78B9]/40 hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
            >
              Talk to an Expert
            </button>
          </motion.div>
        </div>
      </section>

      <LeadCapturePopup open={demoOpen} onOpenChange={setDemoOpen} />

    </div>
  );
}

export function IndustriesHero() {
  const stats = [
    { value: "20+", label: "Industries" },
    { value: "1000+", label: "Clients" },
    { value: "30+", label: "Years" },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-white pt-24 pb-16 md:pt-32 md:pb-24">
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-[#2C78B9]/10 blur-3xl"
        />
        <motion.div
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-[#2C78B9]/8 blur-3xl"
        />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-[#2C78B9]/20 bg-[#2C78B9]/5 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-[#2C78B9] mb-6">
              Mx-ERP Solutions
            </span>

            <h1 className="mb-6 text-balance text-4xl font-extrabold tracking-tight text-slate-900 md:text-5xl lg:text-6xl">
              Purpose-Built ERP for{" "}
              <span className="bg-gradient-to-r from-[#2C78B9] to-[#2C78B9]/60 bg-clip-text text-transparent">
                20+ Industries
              </span>
            </h1>

            <p className="mx-auto max-w-2xl text-pretty text-lg text-slate-600 md:text-xl">
              One integrated ecosystem designed for India&apos;s unique business landscape. From manufacturing to healthcare, we&apos;ve got your operations covered.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-10 flex flex-wrap justify-center gap-6"
          >
            {stats.map((stat, idx) => (
              <div key={idx} className="flex flex-col items-center">
                <span className="text-2xl font-black text-[#2C78B9]">{stat.value}</span>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{stat.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── Extracted dashboard body (used for both mobile/desktop renders) ──────────
function DashboardBody({ dashboard }: { dashboard: any }) {
  return (
    <div className="p-5 overflow-y-auto no-scrollbar">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-[15px] md:text-[20px] font-black text-[#0f172a] leading-tight">
            {dashboard.title}
          </h2>
          <p className="text-[11px] text-[#64748b] font-medium">{dashboard.subtitle}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button className="w-full sm:w-auto px-3 py-1.5 border border-[#e2e8f0] bg-white text-[#475569] text-[11px] font-bold rounded-md">
            Export PDF
          </button>
          <button className="w-full sm:w-auto px-4 py-1.5 bg-[#2C78B9] text-white text-[11px] font-bold rounded-md shadow">
            New Work Order
          </button>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
        {dashboard.kpis.map((kpi: any, i: number) => (
          <div key={i} className="bg-white border border-[#f1f5f9] rounded-[12px] p-2.5 shadow-[0_2px_12px_rgba(0,0,0,0.01)]">
            <div className="flex justify-between items-start mb-1.5">
              <div
                className="w-7 h-7 rounded-[7px] flex items-center justify-center text-white shadow-sm"
                style={{ backgroundColor: kpi.color }}
              >
                <IconRenderer name={kpi.icon} size={14} />
              </div>
              <div className="text-[8px] font-bold text-[#10b981] bg-[#10b981]/10 px-1.5 py-0.5 rounded leading-tight">
                {kpi.trend}
              </div>
            </div>
            <div className="text-[8px] text-[#94a3b8] font-bold tracking-[0.05em] uppercase">{kpi.label}</div>
            <div className="text-[17px] md:text-[20px] font-black text-[#0f172a] mt-0.5">{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3">
        {/* Line chart */}
        <div className="bg-white border border-[#f1f5f9] rounded-[12px] p-3.5 shadow-[0_2px_12px_rgba(0,0,0,0.01)] flex flex-col h-[160px] md:h-[190px]">
          <div className="flex justify-between items-center mb-2.5 shrink-0">
            <div className="text-[10px] font-black text-[#0f172a] tracking-wider uppercase">{dashboard.chartTitle}</div>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dashboard.lineColor || '#3b82f6' }} />
              <span className="text-[10px] font-bold text-[#64748b]">Current</span>
            </div>
          </div>
          <div className="flex-1 w-full relative">
            <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="w-full h-full overflow-visible">
              <defs>
                <linearGradient id="areaGradientHero" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={dashboard.lineColor || '#3b82f6'} stopOpacity="0.2" />
                  <stop offset="100%" stopColor={dashboard.lineColor || '#3b82f6'} stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={dashboard.areaPath || 'M0,25 C15,25 25,18 40,18 C50,18 55,14 65,11 C75,7 85,3 100,5 L100,30 L0,30 Z'} fill="url(#areaGradientHero)" />
              <motion.path
                d={dashboard.linePath || 'M0,25 C15,25 25,18 40,18 C50,18 55,14 65,11 C75,7 85,3 100,5'}
                fill="none"
                stroke={dashboard.lineColor || '#3b82f6'}
                strokeWidth="1.5"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.5, ease: "easeOut", delay: 0.1 }}
              />
            </svg>
          </div>
        </div>

        {/* Bar chart */}
        <div className="bg-white border border-[#f1f5f9] rounded-[12px] p-3.5 shadow-[0_2px_12px_rgba(0,0,0,0.01)] flex flex-col h-[160px] md:h-[190px]">
          <div className="text-[10px] font-black text-[#0f172a] tracking-wider uppercase mb-2.5 shrink-0">{dashboard.barTitle || 'MACHINE LOAD'}</div>
          <div className="flex-1 flex items-end justify-between gap-1.5 h-full">
            {(dashboard.barHeights || [55, 40, 30, 45, 68, 35, 60]).map((height: number, i: number) => (
              <div key={i} className="w-full bg-[#f1f5f9] rounded-t relative flex flex-col justify-end h-full">
                <motion.div
                  className="w-full rounded-t"
                  style={{ backgroundColor: dashboard.barColor || '#e2e8f0' }}
                  initial={{ height: "0%" }}
                  animate={{ height: `${height}%` }}
                  transition={{ duration: 0.6, delay: 0.2 + i * 0.08, ease: "easeOut" }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}